import React, { Fragment } from 'react';

export interface SubNavItem {
  id: string;
  label: string;
  /** Uppercase label rendered above the first item of a new group, e.g. "PROJECT". */
  group?: string;
}

export interface SubNavProps {
  ariaLabel: string;
  items: readonly SubNavItem[];
  active: string;
  onSelect: (id: string) => void;
  className?: string;
}

/**
 * Vertical section nav for a tab group's sub-pages. On screens narrower
 * than `md` it collapses to a `<select>` above the panel so nothing
 * scrolls horizontally.
 */
export const SubNav: React.FC<SubNavProps> = ({ ariaLabel, items, active, onSelect, className = '' }) => {
  let lastGroup: string | undefined;

  return (
    <div className={className}>
      <nav aria-label={ariaLabel} className="hidden md:flex md:flex-col md:gap-0.5">
        {items.map((item) => {
          const showGroupLabel = item.group !== undefined && item.group !== lastGroup;
          lastGroup = item.group;
          return (
            <Fragment key={item.id}>
              {showGroupLabel && (
                <div className="px-3 pt-3 pb-1 text-2xs uppercase tracking-wide text-slate-500">
                  {item.group}
                </div>
              )}
              <button
                type="button"
                data-testid={`section-${item.id}`}
                aria-current={active === item.id ? 'true' : undefined}
                onClick={() => onSelect(item.id)}
                className={
                  'text-left px-3 py-2 rounded-md text-sm transition-colors duration-150 ' +
                  (active === item.id
                    ? 'bg-primary-50 text-primary-700 font-semibold'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900')
                }
              >
                {item.label}
              </button>
            </Fragment>
          );
        })}
      </nav>
      <select
        aria-label={ariaLabel}
        className="md:hidden w-full mb-3 rounded-md border border-slate-300 text-sm py-2 px-2.5 bg-white"
        value={active}
        onChange={(e) => onSelect(e.target.value)}
      >
        {items.map((item) => (
          <option key={item.id} value={item.id}>
            {item.label}
          </option>
        ))}
      </select>
    </div>
  );
};

export default SubNav;
